/* Store-scoped native photo storage adapter; retain CCM's preview, reorder and photo-later flow. */
(function () {
  customElements.whenDefined('ccm-live-preview-v3').then(() => {
    const prototype = customElements.get('ccm-live-preview-v3').prototype;
    const previous = {};
    for (const name of ['openFilePicker', 'uploadLegacyPhoto', 'requestJson', 'handleFormSubmit', 'clearLineProperties', 'connectedCallback', 'applyHandoffRecord', 'restoreCartEdit']) previous[name] = prototype[name];
    const storedAsset = photo => {
      try {
        const url = new URL(photo?.url || '');
        const match = /^\/f\/([a-f0-9-]{36})\/[a-f0-9]{64}\/production\.jpg$/.exec(url.pathname);
        return url.protocol === 'https:' && url.hostname === 'p.crystalclearmemories.com' && match
          ? {asset_id: match[1], url: url.href, status: 'ready'} : null;
      } catch (_) { return null; }
    };
    const restoreArchivedAsset = async (instance, generation) => {
      if (instance.nativeAsset || !instance.file?.restored || instance.cartEditRestored) return;
      const source = instance.legacyPhoto?.url, selectedFile = instance.file;
      if (!source) throw new Error('Your saved photo could not be verified. Choose the photo again.');
      // Earlier CCM orders already have permanent photos in CCM's own S3 bucket.
      // Keep that established restored-photo flow; these are not archive assets.
      try {
        const owned = new URL(source);
        if (owned.protocol === 'https:' && owned.hostname === 'img.crystalclearmemories.com'
            && !owned.username && !owned.password && !owned.port && !owned.search && !owned.hash
            && owned.pathname !== '/' && owned.pathname.length <= 2048) return;
      } catch (_) {}

      const restored = await window.CustomerMedia.adopt(source);
      if (generation !== (instance.photoGeneration || 0) || instance.file !== selectedFile || instance.legacyPhoto?.url !== source) {
        throw new DOMException('Photo selection changed', 'AbortError');
      }
      const identity = storedAsset(restored);
      if (!identity || identity.asset_id !== restored.asset_id || restored.status !== 'ready' || !restored.legacy_archive) {
        throw new Error('Your saved photo could not be verified. Choose the photo again.');
      }
      instance.nativeAsset = restored;
      instance.legacyPhoto = {...instance.legacyPhoto,url:restored.url,handle:restored.asset_id};
      instance.setLineProperty('Photo',restored.url,false);
    };
    prototype.applyHandoffRecord = function (record) {
      previous.applyHandoffRecord.call(this, record);
      this.nativeAsset = storedAsset(this.legacyPhoto);
    };
    prototype.restoreCartEdit = async function (...args) {
      await previous.restoreCartEdit.apply(this, args);
      const asset = storedAsset(this.legacyPhoto);
      if (!asset || !this.cartEditRestored) return;
      const cart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'},
      }, {timeoutMs:30000});
      const line = cart.items?.find(item => {
        const props = this.cartItemProperties(item);
        return props._live_preview_v3_session_id === this.cartEditContext?.sessionId
          && (!this.cartEditContext?.groupId || props['_Group Identify'] === this.cartEditContext.groupId);
      });
      const props = line && this.cartItemProperties(line);
      if (props?._media_asset_id !== asset.asset_id || !/^[a-f0-9]{64}$/.test(props?._media_order_receipt || '')) {
        const message = 'Your saved photo could not be verified. Choose the photo again to continue.';
        this.cartEditRestored = false;
        this.showError(message, true);
        throw new Error(message);
      }
      // The existing cart remains the owner during editing, including a reopened browser.
      this.nativeAsset = null;
      this.setLineProperty('_media_asset_id', props._media_asset_id, true);
      this.setLineProperty('_media_order_receipt', props._media_order_receipt, true);
    };
    prototype.openFilePicker = async function () {
      const generation = this.photoGeneration || 0;
      try {
        if (!window.CustomerMedia || !await window.CustomerMedia.enabled()) throw new Error(this.dataset.copyErrorBody || 'Photo upload is unavailable. Please retry.');
        const choice = await window.CustomerMedia.choose();
        if (!choice || generation !== (this.photoGeneration || 0)) return;
        this.nativeChoice = choice;
        await this.handleFile(choice.file);
      } catch (error) { if (generation === (this.photoGeneration || 0)) this.showError(error.message || this.dataset.copyErrorBody); }
    };
    prototype.uploadLegacyPhoto = async function (file, options = {}) {
      const generation = this.photoGeneration || 0;
      if (!window.CustomerMedia || !await window.CustomerMedia.enabled()) throw new Error(this.dataset.copyErrorBody || 'Photo upload is unavailable. Please retry.');
      if (generation !== (this.photoGeneration || 0) || (this.file && this.file !== file)) return null;
      const choice = this.nativeChoice?.file === file ? this.nativeChoice : null;
      this.nativeChoice = null;
      const asset = choice?.asset || await window.CustomerMedia.upload(file);
      if (generation !== (this.photoGeneration || 0) || (this.file && this.file !== file)) return null;
      this.nativeAsset = asset;
      const legacy = this.rememberLegacyPhoto({url: asset.url, handle: asset.asset_id, filename: file.name,
        mimetype: asset.media_type, size: asset.bytes}, file, options);
      this.setLineProperty('_media_asset_id', asset.asset_id, true);
      return legacy;
    };
    prototype.requestJson = async function (path, options = {}) {
      if (path === this.dataset.createPath && options.body instanceof FormData && options.body.has('image')) {
        const generation=this.photoGeneration || 0;
        await this.legacyUploadPromise;
        await restoreArchivedAsset(this,generation);
        if (generation !== (this.photoGeneration || 0)) throw new DOMException('Photo selection changed','AbortError');
        if (!this.nativeAsset && !this.file?.restored) throw new Error(this.dataset.copyErrorBody || 'Your photo could not be saved. Please retry.');
        if (this.nativeAsset) {
          options.body.delete('image');
          options.body.append('media_asset_id', this.nativeAsset.asset_id);
          options.body.append('owner_token', window.CustomerMedia.ownerToken());
          options.headers = {...options.headers, ...window.CustomerMedia.headers()};
        }
      }
      return previous.requestJson.call(this, path, options);
    };
    prototype.clearLineProperties = function () {
      previous.clearLineProperties.call(this);
      this.nativeAsset = null;
      for (const name of ['_media_asset_id', '_media_order_receipt']) this.productForm?.querySelector(`[name="properties[${name}]"]`)?.remove();
    };
    prototype.handleFormSubmit = async function (event) {
      if (this.allowNextSubmit || !this.file) return previous.handleFormSubmit.call(this, event);
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.nativePreparing) return;
      this.nativePreparing = true;
      const generation = this.photoGeneration || 0;
      try {
        await this.legacyUploadPromise;
        await restoreArchivedAsset(this,generation);
        if (!this.nativeAsset && !this.file?.restored) throw new Error(this.dataset.copyErrorBody || 'Your photo could not be saved. Please retry.');
        if (this.nativeAsset) {
          const cart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart/update.js`, {
            method:'POST', body:'{}', credentials:'same-origin', cache:'no-store',
            headers:{Accept:'application/json','Content-Type':'application/json'},
          }, {timeoutMs:30000});
          let verified;
          const bindingAsset=this.nativeAsset;
          try { verified = await window.CustomerMedia.associate(bindingAsset, cart.token); }
          catch (failure) {
            if (generation !== (this.photoGeneration || 0) || this.nativeAsset?.asset_id!==bindingAsset.asset_id) return;
            const originalId=bindingAsset.asset_id,source=bindingAsset.url;
            const restored=await window.CustomerMedia.adopt(source);
            if (generation !== (this.photoGeneration || 0)) return;
            const identity=storedAsset(restored);
            if (!identity || identity.asset_id!==originalId || restored.asset_id!==originalId || !restored.legacy_archive) throw failure;
            this.nativeAsset=restored;
            verified=await window.CustomerMedia.associate(restored,cart.token);
          }
          if (generation !== (this.photoGeneration || 0) || this.nativeAsset?.asset_id!==bindingAsset.asset_id) return;
          if (verified.asset_id !== this.nativeAsset.asset_id || !/^[a-f0-9]{64}$/.test(verified.order_receipt || '')) throw new Error('Your photo could not be verified. Please retry.');
          this.setLineProperty('Photo', verified.url, false);
          this.setLineProperty('_media_asset_id', verified.asset_id, true);
          this.setLineProperty('_media_order_receipt', verified.order_receipt, true);
        }
        await previous.handleFormSubmit.call(this, event);
      } catch (error) { this.releaseSubmissionUi(); this.showError(error.message || this.dataset.copyCartError, true); }
      finally { this.nativePreparing = false; }
    };
    const attach = instance => {
      if (!instance.productForm || instance.nativeStorageAttached) return;
      if (!instance.nativeAsset && instance.file && !instance.cartEditContext) instance.nativeAsset = storedAsset(instance.legacyPhoto);
      instance.productForm.removeEventListener('submit', instance.handleFormSubmit, true);
      instance.handleFormSubmit = prototype.handleFormSubmit.bind(instance);
      instance.productForm.addEventListener('submit', instance.handleFormSubmit, true);
      instance.nativeStorageAttached = true;
    };
    prototype.connectedCallback = function () { previous.connectedCallback.call(this); attach(this); };
    document.querySelectorAll('ccm-live-preview-v3').forEach(attach);
  });
})();
